import { cx } from 'linaria';
import React, { ReactElement } from 'react';

import Box from '../box';
import { IconChevronDown, IconChevronUp } from '../foundation/icons';
import {
    Avatar,
    AvatarShape,
    Button,
    ButtonVariant,
    SelectOption,
    SelectOptions,
    Logo,
    ThemeProvider,
    usePositioner,
    Variables,
} from '../index';
import * as styles from './global-header.styles';
export interface MenuItem<T> {
    content: T;
    key: string;
    label?: string;
    onClick?: (event: React.MouseEvent<Element>) => void;
    onAuxClick?: (event: React.MouseEvent<Element>) => void;
    onKeyUp?: (event: React.KeyboardEvent<Element>) => void;
    active?: boolean;
    variant?: ButtonVariant;
}

export interface AccountDetails {
    testId?: string;
    displayName: string;
    avatarImageSource?: string;
    avatarShape?: AvatarShape;
    /**
     * Items to pass to SelectOptions component.
     */
    dropdownItems?: SelectOption[];
    showAvatarBadge?: boolean;
}
export interface GlobalHeaderProps {
    customLogo?: ReactElement;
    testId?: string;
    primaryNavigationItems: Array<MenuItem<string>>;
    secondaryNavigationItems?: Array<MenuItem<ReactElement>>;
    accountDetails?: AccountDetails;
    onLogoClick?: (event: React.MouseEvent<Element>) => void;
}

export const GlobalHeader = React.forwardRef<HTMLDivElement, GlobalHeaderProps>((props, ref) => {
    const {
        customLogo,
        testId = 'global-header',
        primaryNavigationItems,
        secondaryNavigationItems,
        accountDetails,
        onLogoClick,
    } = props;

    const dropdownRef = React.useRef(null);
    const [isDropdownVisible, setIsDropdownVisible] = React.useState(false);
    const [primaryActiveItem, setIsPrimaryActiveItem] = React.useState(
        primaryNavigationItems.findIndex((item) => item.active),
    );

    const canHaveAvatarBadge = !!accountDetails?.showAvatarBadge;

    const handleAvatarOnClick = React.useCallback(() => {
        setIsDropdownVisible((isDropdownVisible) => {
            return !isDropdownVisible;
        });
    }, []);

    const hasClickEvent = !!onLogoClick;

    const { getPopperProps, getReferenceProps } = usePositioner({
        placement: 'top-start',
    });

    return (
        <ThemeProvider theme="dark">
            <Box ref={ref} className={cx(styles.root)} data-testid={testId}>
                <Box
                    className={cx(styles.logo, hasClickEvent ? styles['clickable'] : '')}
                    onClick={hasClickEvent ? onLogoClick : null}
                    data-testid="on-logo-click"
                >
                    {customLogo ? customLogo : <Logo width={23} height={48} color={Variables.css.catapultWhite} />}
                </Box>

                <Box className={cx(styles.primaryNavWrapper)}>
                    {primaryNavigationItems.map((item, index) => {
                        const {
                            content,
                            onClick,
                            onKeyUp,
                            key,
                            variant = 'text',
                            onAuxClick,
                            ...passThroughProps
                        } = item;
                        return (
                            <Button
                                key={key}
                                testId={key}
                                className={cx(styles.link, primaryActiveItem == index ? styles['active'] : '')}
                                variant={variant}
                                onAuxClick={(event) => {
                                    onAuxClick && onAuxClick(event);
                                }}
                                onClick={(event) => {
                                    setIsPrimaryActiveItem(index);
                                    onClick && onClick(event);
                                }}
                                onKeyUp={(event) => {
                                    onKeyUp && onKeyUp(event);
                                }}
                                {...passThroughProps}
                            >
                                {content}
                            </Button>
                        );
                    })}
                </Box>

                <Box className={cx(styles.secondaryNavWrapper)} data-testid={'secondary-nav'}>
                    <Box className={cx(styles.secondaryItemsWrapper)}>
                        {secondaryNavigationItems &&
                            secondaryNavigationItems.map((item) => {
                                const {
                                    content,
                                    onClick,
                                    onKeyUp,
                                    key,
                                    variant = 'text',
                                    label,
                                    ...passThroughProps
                                } = item;
                                return (
                                    <Button
                                        key={key}
                                        testId={key}
                                        variant={variant}
                                        size="small"
                                        className={cx(styles.link)}
                                        onClick={onClick}
                                        onKeyUp={onKeyUp}
                                        title={label}
                                        {...passThroughProps}
                                    >
                                        {content}
                                    </Button>
                                );
                            })}
                    </Box>
                </Box>

                {accountDetails && (
                    <>
                        <div ref={dropdownRef}>
                            <Button
                                className={cx(
                                    styles.avatarWrapper,
                                    isDropdownVisible ? styles['selected'] : styles['unselected'],
                                )}
                                onClick={handleAvatarOnClick}
                                testId="account-detail"
                                {...getReferenceProps()}
                            >
                                <Box className={cx(styles.secondaryAvatarWrapper)}>
                                    {canHaveAvatarBadge ? (
                                        <>
                                            <Avatar
                                                testId={accountDetails.testId}
                                                size="large"
                                                shape={accountDetails.avatarShape}
                                                name={accountDetails.displayName}
                                                src={accountDetails.avatarImageSource}
                                            />
                                            <Avatar
                                                testId={`${accountDetails.testId}-badge`}
                                                name={accountDetails.displayName}
                                                size="small"
                                                className={cx(styles.avatarBadgeOffset)}
                                            />
                                        </>
                                    ) : (
                                        <Avatar
                                            testId={accountDetails.testId}
                                            size="large"
                                            shape={accountDetails.avatarShape}
                                            name={accountDetails.displayName}
                                            src={accountDetails.avatarImageSource}
                                        />
                                    )}
                                    <Box
                                        data-testid="dropdown-indicator"
                                        className={cx(
                                            styles.avatarBadgeIndicator,
                                            canHaveAvatarBadge ? styles['badge'] : '',
                                        )}
                                    >
                                        {isDropdownVisible ? <IconChevronUp /> : <IconChevronDown />}
                                    </Box>
                                </Box>
                            </Button>
                        </div>
                        {accountDetails?.dropdownItems && (
                            <SelectOptions
                                popperProps={getPopperProps}
                                parentRef={dropdownRef}
                                setIsVisible={setIsDropdownVisible}
                                isVisible={isDropdownVisible}
                                menuItems={accountDetails?.dropdownItems}
                                onSelectItem={() => {
                                    setIsDropdownVisible(false);
                                }}
                            />
                        )}
                    </>
                )}
            </Box>
        </ThemeProvider>
    );
});

GlobalHeader.displayName = 'GlobalHeader';
